import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, ChevronRight, MapPin, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useEmployeeApi } from "../employeeApi";
import { StatusBadge, PriorityBadge } from "../employeeUi";

const FILTERS = [
    { key: "aktive", label: "Aktive" },
    { key: "afsluttede", label: "Afsluttede" },
    { key: "alle", label: "Alle" },
];

export default function TasksScreen({ onOpenJob }) {
    const api = useEmployeeApi();
    const [jobs, setJobs] = useState(null);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState(() => {
        const f = sessionStorage.getItem("msh_emp_tasks_filter");
        if (f) sessionStorage.removeItem("msh_emp_tasks_filter");
        return f || "aktive";
    });

    useEffect(() => {
        api.getJobs().then((d) => setJobs(d.jobs || [])).catch(() => { setJobs([]); toast.error("Kunne ikke hente opgaver"); });
    }, [api]);

    const shown = useMemo(() => {
        if (!jobs) return [];
        const isDone = (j) => ["færdig", "afsluttet", "done", "completed", "klar", "invoice_ready", "invoiced"].includes((j.status || "").toLowerCase());
        let list = jobs;
        if (filter === "aktive") list = jobs.filter((j) => !isDone(j));
        else if (filter === "afsluttede") list = jobs.filter(isDone);
        const term = q.trim().toLowerCase();
        if (term) list = list.filter((j) => `${j.task} ${j.customer} ${j.address}`.toLowerCase().includes(term));
        return list;
    }, [jobs, filter, q]);

    return (
        <div className="space-y-4" data-testid="employee-tasks">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Opgaver</h1>

            <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="tasks-search"
                    placeholder="Søg opgave, kunde eller adresse…"
                    className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>

            <div className="flex gap-2" data-testid="tasks-filters">
                {FILTERS.map((f) => (
                    <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
                        className={`h-9 px-3 rounded-full text-sm font-semibold border transition-colors ${filter === f.key ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-600"}`}>
                        {f.label}
                    </button>
                ))}
            </div>

            {!jobs ? (
                <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : shown.length === 0 ? (
                <div className="flex flex-col items-center py-16 text-slate-400"><Inbox className="w-10 h-10 mb-3" /><p className="text-sm">Ingen opgaver matcher.</p></div>
            ) : (
                <ul className="space-y-3" data-testid="tasks-list">
                    {shown.map((j) => (
                        <li key={j.id}>
                            <button onClick={() => onOpenJob(j.id)} data-testid={`task-${j.id}`}
                                className="w-full text-left bg-white rounded-2xl border border-slate-200 p-4 shadow-sm active:scale-[0.99] transition-transform">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-semibold text-slate-900 truncate">{j.task}</p>
                                        <p className="text-sm text-slate-500 truncate mt-0.5">{j.customer || "Ukendt kunde"}</p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />
                                </div>
                                {j.address && <p className="flex items-center gap-1.5 text-xs text-slate-500 mt-2"><MapPin className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{j.address}</span></p>}
                                <div className="flex items-center gap-2 mt-3"><StatusBadge status={j.status} /><PriorityBadge priority={j.priority} /></div>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
